import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { NovoContratoWizard } from '@/components/admin/NovoContratoWizard'
import { useUIStore } from '@/stores/ui.store'
import { useContratos, useCancelContrato } from '@/hooks/useContratos'
import { useParceiros } from '@/hooks/useParceiros'
import { useServicos } from '@/hooks/useServicos'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import {
    Search, Plus, FileText, ChevronLeft, ChevronRight, Filter, AlertTriangle, Loader2, Car, User, Handshake, Zap, Calendar
} from 'lucide-react'

export function ContratosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const navigate = useNavigate()
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('todos')
    const [parceiroFilter, setParceiroFilter] = useState('todos')
    const [tipoFilter, setTipoFilter] = useState('todos')
    const [servicoFilter, setServicoFilter] = useState('todos')
    const [ativacaoFilter, setAtivacaoFilter] = useState('todos')
    const [dataDe, setDataDe] = useState('')
    const [dataAte, setDataAte] = useState('')
    const [page, setPage] = useState(1)
    const [showCancelConfirm, setShowCancelConfirm] = useState<string | null>(null)
    const [showWizard, setShowWizard] = useState(false)

    useEffect(() => { setPageTitle('Contratos') }, [setPageTitle])

    const { data: result, isLoading } = useContratos({ 
        search, 
        status: statusFilter, 
        parceiro_id: parceiroFilter !== 'todos' ? parceiroFilter : undefined,
        tipo: tipoFilter as any,
        servico: servicoFilter !== 'todos' ? servicoFilter : undefined,
        ativacao: ativacaoFilter as any,
        data_de: dataDe || undefined,
        data_ate: dataAte || undefined,
        page, 
        pageSize: 10 
    })
    const { data: parceirosRes } = useParceiros()
    const { data: servicosRes } = useServicos({ pageSize: 100 })
    const parceiros = parceirosRes?.data || []
    const servicosList = servicosRes?.data || []
    const cancelMutation = useCancelContrato()

    const contratos = result?.data || []
    const totalPages = result?.totalPages || 1

    const handleCancel = async (id: string) => {
        await cancelMutation.mutateAsync(id)
        setShowCancelConfirm(null)
    }

    const statusCounts = {
        ativo: contratos.filter(c => c.status === 'ativo').length,
        inadimplente: contratos.filter(c => c.status === 'inadimplente').length,
        cancelado: contratos.filter(c => c.status === 'cancelado').length,
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Contratos</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">{result?.total || 0} contratos encontrados</p>
                </div>
                <button onClick={() => setShowWizard(true)} className="btn btn-primary btn-md gap-2">
                    <Plus size={18} /> Novo Contrato
                </button>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                    { label: 'Ativos', value: statusCounts.ativo, color: 'text-success-600 bg-success-50' },
                    { label: 'Inadimplentes', value: statusCounts.inadimplente, color: 'text-warning-600 bg-warning-50' },
                    { label: 'Cancelados', value: statusCounts.cancelado, color: 'text-danger-600 bg-danger-50' },
                ].map((s) => (
                    <div key={s.label} className="glass-card p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                            <FileText size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-surface-900">{s.value}</p>
                            <p className="text-xs text-surface-700/60">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters Row 1 */}
            <div className="glass-card p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="relative md:col-span-2">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input type="text" value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                        placeholder="Buscar por associado ou placa..."
                        className="input pl-10" />
                </div>
                <div className="relative">
                    <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10">
                        <option value="todos">Status: Todos</option>
                        <option value="ativo">Ativo</option>
                        <option value="pendente_adesao">Pend. Adesão</option>
                        <option value="inadimplente">Inadimplente</option>
                        <option value="suspenso">Suspenso</option>
                        <option value="cancelado">Cancelado</option>
                    </select>
                </div>
                <div className="relative">
                    <Car size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={tipoFilter} onChange={(e) => { setTipoFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10">
                        <option value="todos">Tipo: Todos</option>
                        <option value="veicular">Veicular</option>
                        <option value="cpf">Somente CPF</option>
                    </select>
                </div>
                <div className="relative">
                    <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={servicoFilter} onChange={(e) => { setServicoFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10">
                        <option value="todos">Serviço: Todos</option>
                        {Array.from(new Set(servicosList.map(s => s.nome))).map(nome => (
                            <option key={nome} value={nome}>{nome}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Filters Row 2 */}
            <div className="glass-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3 mt-[-1.5rem]">
                <div className="relative">
                    <Zap size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={ativacaoFilter} onChange={(e) => { setAtivacaoFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10">
                        <option value="todos">Ativação: Todas</option>
                        <option value="manual">Atuação Manual</option>
                        <option value="integrada">Integrada / API</option>
                    </select>
                </div>
                <div className="relative md:col-start-2">
                    <Handshake size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <select value={parceiroFilter} onChange={(e) => { setParceiroFilter(e.target.value); setPage(1) }}
                        className="input pl-9 pr-10">
                        <option value="todos">Parceiros: Todos</option>
                        {parceiros.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                    </select>
                </div>
                <div className="relative flex items-center gap-2 md:col-span-2">
                    <Calendar size={16} className="text-surface-700/40" />
                    <div className="flex items-center gap-2 flex-1">
                        <input type="date" value={dataDe} onChange={(e) => { setDataDe(e.target.value); setPage(1) }}
                            className="input text-xs" title="Data inicial" />
                        <span className="text-surface-700/40">até</span>
                        <input type="date" value={dataAte} onChange={(e) => { setDataAte(e.target.value); setPage(1) }}
                            className="input text-xs" title="Data final" />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : contratos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <FileText size={40} className="text-surface-700/20 mb-3" />
                        <p className="text-surface-700/60">Nenhum contrato encontrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Chave / Associado</th>
                                    <th>Serviço</th>
                                    <th>Veículo</th>
                                    <th>Valor Mensal</th>
                                    <th>Início</th>
                                    <th>Vencimento</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contratos.map((c) => (
                                    <tr key={c.id} className="hover:bg-surface-50 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/contratos/${c.id}`)}>
                                        {/* Chave PDA + Associado */}
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                    <User size={16} className="text-primary-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-surface-900 text-sm font-mono">
                                                        {c.external_reference || <span className="text-surface-400 font-sans">Sem chave</span>}
                                                    </p>
                                                    <p className="font-medium text-surface-800 text-xs truncate">{c.associado_nome || '—'}</p>
                                                    {c.associado_cpf && (
                                                        <p className="text-xs text-surface-700/40">{c.associado_cpf}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        {/* Serviço & Canal */}
                                        <td>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm font-semibold text-surface-900">
                                                    {(c.metadata as any)?.servico_nome || c.plano || <span className="text-surface-400">—</span>}
                                                </span>
                                                {(c.metadata as any)?.origem && (
                                                    <span className="text-[10px] uppercase font-bold text-primary-600">
                                                        {(c.metadata as any)?.parceiro_nome || (c.metadata as any).origem.replace('_', ' ')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {/* Veículo */}
                                        <td>
                                            {c.veiculo_placa ? (
                                                <div>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Car size={14} className="text-surface-700/40" />
                                                        <span className="font-mono text-sm font-semibold">{c.veiculo_placa}</span>
                                                    </span>
                                                    <p className="text-xs text-surface-700/50">
                                                        {[c.veiculo_marca, c.veiculo_modelo].filter(Boolean).join(' ')}
                                                    </p>
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 text-sm text-surface-700/50">
                                                    <User size={14} className="text-blue-500" />
                                                    Somente CPF
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <p className="font-semibold text-surface-900">
                                                {formatCurrency(c.ultima_fatura_valor ?? c.valor_total_mensal)}
                                            </p>
                                            {c.ultima_fatura_status && (
                                                <p className="text-xs text-surface-700/40">{c.ultima_fatura_status}</p>
                                            )}
                                        </td>
                                        <td className="text-sm text-surface-700">{formatDate(c.data_inicio)}</td>
                                        <td className="text-sm text-surface-700">Dia {c.dia_vencimento}</td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(c.status)}`}>
                                                {translateStatus(c.status)}
                                            </span>
                                        </td>
                                        <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                            {c.status === 'ativo' && (
                                                <button
                                                    onClick={() => setShowCancelConfirm(c.id)}
                                                    className="text-xs px-3 py-1.5 rounded-lg border border-danger-200 text-danger-600 hover:bg-danger-50 transition-colors"
                                                >
                                                    Cancelar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-surface-100">
                        <p className="text-sm text-surface-700/60">Página {page} de {totalPages}</p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                )}
            </div>



            {/* Cancel Confirmation */}
            {showCancelConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="glass-card p-6 w-full max-w-sm mx-4 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-danger-100 flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={28} className="text-danger-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-surface-900 mb-2">Cancelar Contrato?</h3>
                        <p className="text-sm text-surface-700/60 mb-6">
                            Esta ação irá cancelar o contrato, desativar os serviços vinculados e cancelar faturas pendentes.
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowCancelConfirm(null)} className="btn flex-1 border border-surface-200 hover:bg-surface-50">
                                Voltar
                            </button>
                            <button onClick={() => handleCancel(showCancelConfirm)}
                                disabled={cancelMutation.isPending}
                                className="btn flex-1 bg-danger-600 text-white hover:bg-danger-700">
                                {cancelMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Novo Contrato Wizard */}
            {showWizard && <NovoContratoWizard onClose={() => setShowWizard(false)} />}
        </div>
    )
}
